import { DataManager, Query } from "@syncfusion/ej2-data"
import { swalWithConfirmAndCancelButtons } from "components/custom/swal"
import SyncfusionGrid from "components/custom/Syncfusion/SyncfusionGrid"
import React, { useEffect, useRef, useState } from "react"
import Loader from "react-loaders"
import { toast } from "react-toastify"
import { Col, FormFeedback, Input, Label, Modal, Row } from "reactstrap"
import api from "services/api.service"
import colors from "utils/colors"

const ManageListSection = ({ scenarioId, selectedManageableList }) => {
  const [listItems, setListItems] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [newItemName, setNewItemName] = useState("")
  const [hasNameError, setHasNameError] = useState(false)

  useEffect(() => {
    if (selectedManageableList.value != -1) {
      loadList(true)
    }
  }, [selectedManageableList])

  const loadList = async toggleLoading => {
    if (toggleLoading) setIsLoading(true)
    let lst = await api.getScenarioList(
      scenarioId,
      selectedManageableList.value
    )
    setListItems(lst)
    setIsLoading(false)
  }

  const addListItem = () => {
    setNewItemName("")
    setHasNameError(false)
    toggleModal()
  }

  const saveNewItem = async () => {
    if (newItemName == "") {
      setHasNameError(true)
    } else {
      await api.createScenarioListItem(
        scenarioId,
        selectedManageableList.value,
        { listItemName: newItemName }
      )
      toggleModal()
      await loadList(true)
    }
  }

  const updateListItem = async item => {
    await api.updateScenarioListItem(scenarioId, selectedManageableList.value, {
      ...item,
      inactive:
        item.inactive == null ||
        item.inactive == "false" ||
        item.inactive == false
          ? false
          : true,
    })
    await loadList()
  }

  const deleteListItem = async item => {
    swalWithConfirmAndCancelButtons
      .fire({
        title: `Are you sure you want to delete this item?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes",
        cancelButtonText: "Cancel",
        reverseButtons: true,
      })
      .then(async result => {
        if (result.isConfirmed) {
          let ret = await api.deleteScenarioListItem(
            item.listItemID,
            selectedManageableList.value
          )
          toast.success(
            ret == -1
              ? "Item deleted successfully"
              : "The item was inactivated because it is in use"
          )
        }
        await loadList()
      })
  }

  const columns = [
    {
      headerText: selectedManageableList.text,
      field: "listItemName",
    },
    {
      headerText: "State",
      field: "inactive",
      template: data => (data.inactive ? "Inactive" : "Active"),
      editType: "dropdownedit",
      edit: {
        params: {
          actionComplete: () => false,
          dataSource: new DataManager([
            { id: 1, value: false, text: "Active" },
            { id: 2, value: true, text: "Inactive" },
          ]),
          fields: { text: "text", value: "value" },
          query: new Query(),
        },
      },
    },
    {
      headerText: "Order",
      field: "orderID",
      width: 50,
      allowEditing: false,
    },
  ]

  const toggleModal = () => {
    setIsModalOpen(!isModalOpen)
  }

  const onActionComplete = args => {
    if (args.requestType === "save") {
      updateListItem(args.data)
    } else if (args.requestType === "delete") {
      deleteListItem(args.data[0])
    }
  }

  const onRowDrop = async args => {
    // debugger
    let reorderedData = [...listItems]
    const [movedRow] = reorderedData.splice(args.fromIndex, 1)
    reorderedData.splice(args.dropIndex, 0, movedRow)

    reorderedData = reorderedData.map((item, index) => {
      return {
        ...item,
        isDirty: item.orderID !== index + 1,
        orderID: index + 1,
      }
    })
    let itemsToUpdateTasks = reorderedData
      .filter(x => x.isDirty)
      .map(x => async () => {
        return await api.updateScenarioListItem(
          scenarioId,
          selectedManageableList.value,
          x
        )
      })
    if (itemsToUpdateTasks.length > 0) {
      await Promise.all(itemsToUpdateTasks.map(t => t()))
      await loadList()
    }
  }

  return (
    <React.Fragment>
      {selectedManageableList.value != -1 && (
        <button
          onClick={addListItem}
          className="btn btn-primary"
          style={{ marginBottom: "10px" }}
        >
          + Add {selectedManageableList.text}
        </button>
      )}
      {isLoading && (
        <Loader
          type="line-scale-pulse-out"
          color={colors.primary}
          style={{ textAlign: "center" }}
        />
      )}
      {!isLoading &&
        listItems.length > 0 &&
        selectedManageableList.value != -1 && (
          <Row>
            <Col sm="12">
              <SyncfusionGrid
                columns={columns}
                data={listItems}
                editSettings={{
                  allowEditing: true,
                  allowAdding: false,
                  allowDeleting: true,
                  mode: "Normal",
                }}
                toolbar={["Edit", "Delete", "Update", "Cancel"]}
                actionComplete={onActionComplete}
                allowRowDragAndDrop={true}
                onRowDrop={onRowDrop}
                sortSettings={{
                  columns: [{ field: "orderID", direction: "Ascending" }],
                }}
              />
            </Col>
          </Row>
        )}
      <Modal
        backdrop="static"
        isOpen={isModalOpen}
        size="md"
        toggle={() => {
          toggleModal()
        }}
      >
        <div className="modal-header">
          <h5 className="modal-title mt-0" id="myModalLabel">
            Add {selectedManageableList.text}
          </h5>
          <button
            type="button"
            onClick={() => {
              toggleModal()
            }}
            className="close"
            data-dismiss="modal"
            aria-label="Close"
          >
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
        <div className="modal-body">
          <div className="mb-3">
            <Label className="form-label">Name</Label>
            <Input
              name="name"
              type="text"
              value={newItemName}
              onChange={e => {
                setNewItemName(e.target.value)
                setHasNameError(false)
              }}
              invalid={hasNameError}
            />
            {hasNameError ? (
              <FormFeedback type="invalid">Name is required</FormFeedback>
            ) : null}
          </div>
        </div>
        <div className="modal-footer">
          <button
            type="button"
            className="btn btn-primary save-user"
            onClick={saveNewItem}
          >
            Save
          </button>
        </div>
      </Modal>
    </React.Fragment>
  )
}

export default ManageListSection
